/** Editor для Надходження з переробки (Порція 4b). 2 таблиці: materials з 206 + products на 26. */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { accounts as accountsApi } from '@/api/documents';
import {
  processingReceipts,
  type ProcessingReceiptMaterialIn,
  type ProcessingReceiptProductIn,
  type ProcessingReceiptReturnedMaterialIn,
  type ProcessingReceiptServiceIn,
} from '@/api/processingReceipts';
import { departmentsApi } from '@/api/hr';
import { nomenclatureGroups } from '@/api/nomenclatureGroups';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import QuickNomenclatureModal from '@/shared/components/QuickNomenclatureModal';
import type { GoodsBalanceRow } from '@/api/documents';
import SearchSelect from '@/components/SearchSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import { contractLabel } from '@/shared/utils/labels';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.processing.receipt.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  processor_contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  processing_service_amount: string;
  processing_service_vat: string;
  comment: string;
  project_id: string | null;
  department_id: string;
  nomenclature_group_id: string;
  materials: ProcessingReceiptMaterialIn[];
  products: ProcessingReceiptProductIn[];
  services: ProcessingReceiptServiceIn[];
  returned_materials: ProcessingReceiptReturnedMaterialIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  processor_contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  processing_service_amount: '0',
  processing_service_vat: '0',
  comment: '',
  project_id: null,
  department_id: '',
  nomenclature_group_id: '',
  materials: [],
  products: [],
  services: [],
  returned_materials: [],
};

const newM = (n: number): ProcessingReceiptMaterialIn => ({ line_number: n, nomenclature_id: '', quantity: '0' });
const newP = (n: number): ProcessingReceiptProductIn => ({ line_number: n, nomenclature_id: '', quantity: '0' });
const newS = (n: number): ProcessingReceiptServiceIn => ({
  line_number: n, nomenclature_id: null, description: '', quantity: '1',
  price: '0', amount: '0', vat_amount: '0',
});
const newR = (n: number): ProcessingReceiptReturnedMaterialIn => ({ line_number: n, nomenclature_id: '', quantity: '0' });

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function ProcessingReceiptEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll() });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'materials' | 'products' | 'services' | 'returned' | 'accounts'>('products');
  // ч.685: підбір/швидке створення номенклатури для АКТИВНОЇ вкладки ТЧ.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickNomOpen, setQuickNomOpen] = useState(false);
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.processor_contractor_id],
    queryFn: () => refsApi.contracts({
      organization_id: form.organization_id,
      contractor_id: form.processor_contractor_id,
    }),
    enabled: !!form.organization_id && !!form.processor_contractor_id,
  });
  const deptQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const groupsQ = useQuery({
    queryKey: ['nomenclature-groups'],
    queryFn: () => nomenclatureGroups.list(),
  });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['processing-receipt', id],
    queryFn: () => processingReceipts.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.processing_receipts', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        processor_contractor_id: d.processor_contractor_id,
        contract_id: d.contract_id ?? '',
        warehouse_id: d.warehouse_id,
        processing_service_amount: d.processing_service_amount,
        processing_service_vat: d.processing_service_vat,
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        department_id: d.department_id ?? '',
        nomenclature_group_id: d.nomenclature_group_id ?? '',
        materials: d.materials.map((m) => ({ line_number: m.line_number, nomenclature_id: m.nomenclature_id, quantity: m.quantity })),
        products: d.products.map((p) => ({
          line_number: p.line_number, nomenclature_id: p.nomenclature_id,
          quantity: p.quantity, account_code: p.account_code ?? null,
          planned_amount: p.planned_amount ?? null,
        })),
        services: (d.services ?? []).map((sv) => ({
          line_number: sv.line_number, nomenclature_id: sv.nomenclature_id ?? null,
          description: sv.description ?? '', quantity: sv.quantity ?? '1',
          price: sv.price ?? '0', amount: sv.amount ?? '0',
          vat_amount: sv.vat_amount ?? '0',
        })),
        returned_materials: (d.returned_materials ?? []).map((rm) => ({
          line_number: rm.line_number, nomenclature_id: rm.nomenclature_id,
          quantity: rm.quantity,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const nomName = (nid: string) => {
    const n = nomQ.data?.items.find((x) => x.id === nid);
    return n ? `${n.name}${n.code ? ' — ' + n.code : ''}` : '—';
  };
  const acctItems = acctList.map((a) => ({ id: a.code, label: `${a.code} — ${a.name}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    processor_contractor_id: form.processor_contractor_id,
    contract_id: form.contract_id || null,
    warehouse_id: form.warehouse_id,
    processing_service_amount: form.processing_service_amount,
    processing_service_vat: form.processing_service_vat,
    comment: form.comment || null,
        project_id: form.project_id,
    department_id: form.department_id || null,
    nomenclature_group_id: form.nomenclature_group_id || null,
    materials: form.materials,
    products: form.products,
    services: form.services,
    returned_materials: form.returned_materials,
  });

  const create = useMutation({
    mutationFn: () => processingReceipts.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-receipts'] });
      navigate(`/processing-receipts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => processingReceipts.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-receipts'] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await processingReceipts.create({ organization_id: form.organization_id, ...buildPayload() });
      try {
        await processingReceipts.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/processing-receipts/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-receipts'] });
      navigate(`/processing-receipts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await processingReceipts.update(id!, buildPayload());
      await processingReceipts.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-receipts'] }),
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => processingReceipts.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-receipts'] }),
    onError: (e) => setError(extractError(e)),
  });
  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => processingReceipts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['processing-receipts'] });
      navigate('/processing-receipts');
    },
    onError: (e) => setError(extractError(e)),
  });

  function rmM(i: number) {
    setForm((f) => ({ ...f, materials: f.materials.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setM(i: number, patch: Partial<ProcessingReceiptMaterialIn>) {
    setForm((f) => ({ ...f, materials: f.materials.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function rmP(i: number) {
    setForm((f) => ({ ...f, products: f.products.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setP(i: number, patch: Partial<ProcessingReceiptProductIn>) {
    setForm((f) => ({ ...f, products: f.products.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function rmS(i: number) {
    setForm((f) => ({ ...f, services: f.services.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setS(i: number, patch: Partial<ProcessingReceiptServiceIn>) {
    setForm((f) => ({ ...f, services: f.services.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function rmR(i: number) {
    setForm((f) => ({ ...f, returned_materials: f.returned_materials.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setR(i: number, patch: Partial<ProcessingReceiptReturnedMaterialIn>) {
    setForm((f) => ({ ...f, returned_materials: f.returned_materials.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  // ч.685: рядки з попапа мультивибору — у ТЧ активної вкладки.
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => {
      if (tab === 'products') {
        return { ...f, products: [...f.products, ...rows.map((r, k) => ({
          ...newP(f.products.length + 1 + k), nomenclature_id: r.nomenclature_id,
          quantity: '1',
        }))] };
      }
      if (tab === 'services') {
        return { ...f, services: [...f.services, ...rows.map((r, k) => ({
          ...newS(f.services.length + 1 + k), nomenclature_id: r.nomenclature_id,
        }))] };
      }
      if (tab === 'returned') {
        return { ...f, returned_materials: [...f.returned_materials, ...rows.map((r, k) => ({
          ...newR(f.returned_materials.length + 1 + k), nomenclature_id: r.nomenclature_id,
          quantity: '1',
        }))] };
      }
      return { ...f, materials: [...f.materials, ...rows.map((r, k) => ({
        ...newM(f.materials.length + 1 + k), nomenclature_id: r.nomenclature_id,
        quantity: '1',
      }))] };
    });
  }
  // ч.911: кнопки ТЧ — у шапці блоку (діють на АКТИВНУ вкладку; на вкладці
  // «Рахунки обліку» та у проведеному документі — не показуються, як і було).
  const addButtons = !isPosted && tab !== 'accounts' ? (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <button type="button" className="primary" onClick={() => setPickerOpen(true)}
        disabled={!form.organization_id}
        title={!form.organization_id ? 'Спершу оберіть юрособу'
          : 'Підбір з довідника номенклатури (мультивибір)'}>
        + додати номенклатуру
      </button>
      <button type="button" className="btn-doc" onClick={() => setQuickNomOpen(true)}
        title="Створити нову номенклатуру (мінімум полів) і одразу підставити рядком">
        створити номенклатуру
      </button>
    </div>
  ) : undefined;
  const svcTotal = form.services.reduce((acc, sv) => acc + (Number(sv.amount) || 0), 0);
  const svcVatTotal = form.services.reduce((acc, sv) => acc + (Number(sv.vat_amount) || 0), 0);

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  // ч.911 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'processor', title: 'Переробник і склад', required: 2,
      filled: (form.processor_contractor_id ? 1 : 0) + (form.warehouse_id ? 1 : 0) },
    { id: 'analytics', title: 'Підрозділ і номенклатурна група', required: 0, filled: 0 },
    { id: 'tables', title: 'Табличні частини', required: 1,
      filled: form.products.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове надходження з переробки' : `Надходження: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.processing_receipts', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/processing-receipts')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/processing-receipts/${id}/print`)}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.processing_receipts', id: isNew ? '' : id! }} />
      }>
      {/* ч.679 (канон): [Юрособа|Номер|Дата] → [Переробник|Договір|Склад] →
          [Підрозділ|Номгрупа]. Послуги — окремою вкладкою ТЧ. ч.911 — блоками. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })} disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Переробник і склад">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Переробник *</label>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.edrpou ?? null }))}
            value={form.processor_contractor_id}
            onChange={(v) => setForm({ ...form, processor_contractor_id: v, contract_id: '' })}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>
            Договір{' '}
            <a href={`/refs/contracts/new${form.processor_contractor_id ? `?contractor_id=${form.processor_contractor_id}` : ''}`}
               target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', fontWeight: 'normal',
               color: 'var(--primary)', marginLeft: 6 }}>створити ↗</a>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.processor_contractor_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Склад призначення *</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.name} — {w.code}</option>))}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Підрозділ і номенклатурна група">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Підрозділ</label>
          <select value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {deptQ.data?.items.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Номенклатурна група</label>
          <select value={form.nomenclature_group_id}
                  onChange={(e) => setForm({ ...form, nomenclature_group_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {(groupsQ.data ?? []).map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={4} title="Табличні частини" actions={addButtons}>
      {/* Табличні частини — вкладки (канон seg-tabs, «Рахунки обліку» справа) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={tab === 'products' ? 'active' : ''}
            onClick={() => setTab('products')}>Продукція ({form.products.length})</button>
          <button type="button" className={tab === 'services' ? 'active' : ''}
            onClick={() => setTab('services')}>Послуги ({form.services.length})</button>
          <button type="button" className={tab === 'materials' ? 'active' : ''}
            onClick={() => setTab('materials')}>Використані матеріали ({form.materials.length})</button>
          <button type="button" className={tab === 'returned' ? 'active' : ''}
            onClick={() => setTab('returned')}>Повернені матеріали ({form.returned_materials.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={tab === 'accounts' ? 'active' : ''}
            onClick={() => setTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {tab === 'accounts' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th style={{ width: 120 }}>Розділ</th>
                <th>Номенклатура</th>
                <th style={{ width: 220 }}>Рах. обліку</th>
              </tr>
            </thead>
            <tbody>
              {form.products.filter((p) => p.nomenclature_id).map((p) => {
                const i = form.products.indexOf(p);
                return (
                  <tr key={`p${i}`}>
                    <td>Продукція</td>
                    <td>{nomName(p.nomenclature_id)}</td>
                    <td>{acctSelect(p.account_code, (v) => setP(i, { account_code: v }), '26')}</td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожньо — рахунок за замовчуванням (вказаний у дужках). Оберіть свій, щоб перебити.
          </p>
        </div>
      )}

      {tab === 'materials' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Сировина</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.materials.map((m, i) => (
                <tr key={i}>
                  <td>{m.line_number}</td>
                  <td>
                    <NomenclatureSelect value={m.nomenclature_id}
                      onChange={(v) => setM(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td>
                    <input type="number" step="0.001" value={m.quantity}
                           onChange={(e) => setM(i, { quantity: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmM(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {tab === 'products' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Продукція</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 130 }}
                    title="База розподілу собівартості (сировина+послуги) між продуктами">
                  Планова сума</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.products.map((p, i) => (
                <tr key={i}>
                  <td>{p.line_number}</td>
                  <td>
                    <NomenclatureSelect value={p.nomenclature_id}
                      onChange={(v) => setP(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td>
                    <input type="number" step="0.001" value={p.quantity}
                           onChange={(e) => setP(i, { quantity: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={p.planned_amount ?? ''}
                           onChange={(e) => setP(i, { planned_amount: e.target.value || null })}
                           disabled={isPosted} style={{ textAlign: 'right' }} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmP(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {tab === 'services' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 220 }}>Номенклатура (послуга)</th>
                <th>Зміст</th>
                <th style={{ width: 90 }}>К-сть</th>
                <th style={{ width: 100 }}>Ціна</th>
                <th style={{ width: 110 }}>Сума</th>
                <th style={{ width: 90 }}>Ставка ПДВ</th>
                <th style={{ width: 100 }}>ПДВ</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.services.map((sv, i) => (
                <tr key={i}>
                  <td>{sv.line_number}</td>
                  <td>
                    <NomenclatureSelect value={sv.nomenclature_id ?? ''}
                      onChange={(v) => setS(i, { nomenclature_id: v || null })}
                      disabled={isPosted} kind="services" />
                  </td>
                  <td>
                    <input value={sv.description ?? ''}
                           onChange={(e) => setS(i, { description: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.001" value={sv.quantity ?? '1'}
                           onChange={(e) => {
                             const q = e.target.value;
                             const amt = (Number(q) || 0) * (Number(sv.price) || 0);
                             setS(i, { quantity: q, amount: amt ? String(amt.toFixed(2)) : sv.amount });
                           }} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={sv.price ?? ''}
                           onChange={(e) => {
                             const pr = e.target.value;
                             const amt = (Number(pr) || 0) * (Number(sv.quantity) || 0);
                             setS(i, { price: pr || null, amount: amt ? String(amt.toFixed(2)) : sv.amount });
                           }} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={sv.amount ?? '0'}
                           onChange={(e) => {
                             const amt = e.target.value;
                             const rate = Number(sv.vat_rate_percent);
                             setS(i, {
                               amount: amt,
                               ...(sv.vat_rate_percent != null && sv.vat_rate_percent !== ''
                                 ? { vat_amount: ((Number(amt) || 0) * rate / 100).toFixed(2) }
                                 : {}),
                             });
                           }}
                           disabled={isPosted} style={{ textAlign: 'right' }} />
                  </td>
                  <td>
                    <select value={sv.vat_rate_percent ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setS(i, {
                                vat_rate_percent: v || null,
                                ...(v !== ''
                                  ? { vat_amount: ((Number(sv.amount) || 0) * Number(v) / 100).toFixed(2) }
                                  : {}),
                              });
                            }}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">—</option>
                      <option value="20">20%</option>
                      <option value="14">14%</option>
                      <option value="7">7%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.01" value={sv.vat_amount ?? '0'}
                           onChange={(e) => setS(i, { vat_amount: e.target.value })}
                           disabled={isPosted} style={{ textAlign: 'right' }} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmS(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
            {form.services.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 600 }}>
                  <td colSpan={5} style={{ textAlign: 'right' }}>Разом послуг:</td>
                  <td style={{ textAlign: 'right' }}>{svcTotal.toFixed(2)}</td>
                  <td />
                  <td style={{ textAlign: 'right' }}>{svcVatTotal.toFixed(2)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Сума послуг переробника лягає у собівартість продукції (Дт 26 — Кт 631).
          </p>
        </div>
      )}

      {tab === 'returned' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Матеріал (повернення з переробки)</th>
                <th style={{ width: 180 }}>Склад повернення</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.returned_materials.map((rm, i) => (
                <tr key={i}>
                  <td>{rm.line_number}</td>
                  <td>
                    <NomenclatureSelect value={rm.nomenclature_id}
                      onChange={(v) => setR(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td>
                    <select value={rm.warehouse_id ?? ''}
                            onChange={(e) => setR(i, { warehouse_id: e.target.value || null })}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">— склад шапки —</option>
                      {warehousesQ.data?.map((w) => (
                        <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.001" value={rm.quantity}
                           onChange={(e) => setR(i, { quantity: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmR(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Невикористана сировина повертається від переробника: списується з 206
            (WAVG) і оприбутковується на склад призначення (Дт 201 — Кт 206).
          </p>
        </div>
      )}

      {/* ч.685: попап підбору (мультивибір; послуги — з довідника послуг). */}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          catalogMode
          onlyKind={tab === 'services' ? 'services' : 'goods'}
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {quickNomOpen && (
        <QuickNomenclatureModal
          onClose={() => setQuickNomOpen(false)}
          onCreated={(nom) => {
            setQuickNomOpen(false);
            addRowsFromPicker([{
              warehouse_id: '', warehouse_code: '', warehouse_name: '',
              nomenclature_id: nom.id, nomenclature_code: nom.code,
              nomenclature_article: null, nomenclature_name: nom.name,
              quantity: '0', cost: null,
            } as GoodsBalanceRow]);
          }}
        />
      )}

      </DocBlock>

      <DocBlock index={5} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.processing_receipts" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
